import React, { Component } from "react";
import Modal, { MODAL_SIZE } from "@acadia-ui/modal";
import Dropdown, { OPTION_DISPLAY } from "@acadia-ui/dropdown";
import Textbox from "@acadia-ui/textbox";
import Button, { BUTTON_DISPLAY } from "@acadia-ui/button";

import "./MultiplierModal.scss";
import TableHelper from "../../Components/Table/TableHelper";
import { VALIDATION_ERROR_MESSAGE } from "../../Components/FormBuilder/ChangeEvents";

const typeIDMapping = {
	"Line Item": ["LineItemID", "LineItemName"],
	"Ad Set": ["AdSetID", "AdSetName"],
	Placement: ["PlacementID", "TraffickingRequestGroupNumbers", "TraffickingPlacementStatusID", "PlatformPlacementPK", "GoogleCampaignManagerPK"],
	Creative: ["CreativeID", "CreativeName"],
	"Insertion Order": ["InsertionOrderID"]
};

class MultiplierModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedOptionsArray: [{ dataKey: null, dataValues: [] }],
			multipliedRowsCount: 0,
			freeFormFields: {}
		};
	}

	modalCleanupOnClose = () => {
		this.setState(
			{
				selectedOptionsArray: [{ dataKey: null, dataValues: [] }],
				multipliedRowsCount: 0,
				freeFormFields: {}
			},
			() => {
				Modal.hide(`${this.props.name}_multiplier`);
			}
		);
	};

	performMultiplication = () => {
		const platformFieldsToDelete = [
			"TraffickingWorkflowStatusID",
			"TraffickingRequestID",
			"TraffickingRequestGroupNumbers",
			"TraffickingPlacementStatusID",
			"PlatformPlacementPK",
			"PlatformPackagePK",
			"PlatformInsertionOrderPK",
			"PlatformLineItemPK",
			"ExternalSystemStatusID",
			"GoogleCampaignManagerPK"
		];
		const rowToDupe = JSON.parse(JSON.stringify(this.props.data));
		rowToDupe.CampaignID = null;
		rowToDupe.TaxonomyData = null;
		rowToDupe.CreatedBy = null;
		rowToDupe.UpdatedBy = null;
		rowToDupe.UniqueID = null;
		rowToDupe.Row = null;
		rowToDupe.Duplicate = true;
		rowToDupe.isEdited = true;

		platformFieldsToDelete.forEach((fieldName) => {
			delete rowToDupe[fieldName];
		});
		if (typeIDMapping[this.props.type]) {
			typeIDMapping[this.props.type].forEach((field) => {
				delete rowToDupe[field];
			});
		}

		if (rowToDupe.MasterPlatformID == 5) {
			rowToDupe.ExternalSystemStatusID = 1;
			rowToDupe.TraffickingPlacementStatusID = null;
			rowToDupe.ExternalSystemResponse = "Not Synced";
		}

		const columns = [];
		Object.keys(this.props.config).forEach((categoryName) => {
			if (categoryName !== "RegEx" && categoryName !== "ChildData") {
				this.props.config[categoryName]?.forEach?.((dataKey) => {
					if (dataKey.IsSummarizedHeader && dataKey.FieldType !== "Hidden") {
						columns.push(dataKey);
					}
				});
			}
		});
		const fieldNamesObject = {};
		const fieldValuesObject = {};
		this.state.selectedOptionsArray.forEach((option, index) => {
			fieldNamesObject[index] = columns.find((column) => {
				return column.Label == option.dataKey.label;
			})?.Name;
			fieldValuesObject[index] = option.dataValues;
		});
		const dataWithMultipliedRows = TableHelper.handleDataMultiplier(
			fieldNamesObject,
			fieldValuesObject,
			rowToDupe,
			JSON.parse(JSON.stringify(this.props.allRows)),
			this.state.multipliedRowsCount,
			this.props.hierarchyLevel
		);
		this.props.handleMultiply(dataWithMultipliedRows);
		this.modalCleanupOnClose();
	};

	buildAdvancedOptions = () => {
		const buildAdvancedOptions = [];
		this.state.selectedOptionsArray.forEach((selectedOption, index) => {
			buildAdvancedOptions.push(this.returnAdvancedDropdown(selectedOption, index));
		});
		return buildAdvancedOptions;
	};

	createActions = (i) => {
		return (
			<div className="multiplierActions">
				{i === this.state.selectedOptionsArray.length - 1 ? (
					<Button display={BUTTON_DISPLAY.ICON} onClick={this.addField} icon="icon-add" data-tracking-id-alt="add-multiplier-field">
						Add
					</Button>
				) : (
					<Button display={BUTTON_DISPLAY.ICON} onClick={(event) => this.removeField(i)} icon="icon-delete" data-tracking-id-alt="remove-multiplier-field">
						Delete
					</Button>
				)}
			</div>
		);
	};

	returnAdvancedDropdown = (selectedOption, i) => {
		if (this.props.config) {
			const excludedFields = ["Rate Type", "Do Not Use"];
			const dropdownFields = [];
			const freeFormFields = [];
			const fieldsWithOptions = {};
			const columns = [];
			const selectedFields = [];
			this.state.selectedOptionsArray.forEach((option) => {
				if (option.dataKey) {
					selectedFields.push(option.dataKey.label);
				}
			});
			Object.keys(this.props.config).forEach((categoryName) => {
				if (categoryName !== "RegEx" && categoryName !== "ChildData") {
					this.props.config[categoryName]?.forEach?.((dataKey) => {
						if (dataKey.IsSummarizedHeader && dataKey.FieldType !== "Hidden") {
							columns.push(dataKey);
						}
					});
				}
			});

			columns.forEach((column) => {
				if (column.Name === "CreativeAgencyOwner") {
					return;
				}
				if ((column.FieldType === "Select" || column.DataType === "Select") && column.Name !== "ParentDescription") {
					fieldsWithOptions[column.Label] = column.Options.map((option) => {
						return { value: option.OptionID, label: option.OptionLabel };
					});
					if (!selectedFields.includes(column.Label) && excludedFields.indexOf(column.Label) == -1) {
						dropdownFields.push({ label: column.Label, value: column.Name, type: "select" });
					}
				} else if (column.DataType === "String") {
					if (!selectedFields.includes(column.Label)) {
						dropdownFields.push({ label: column.Label, value: column.Name, type: "text" });
					}
					freeFormFields.push(column.Label);
				}
			});
			const fieldLabel = selectedOption.dataKey ? selectedOption.dataKey?.label : null;
			const fieldValidationPattern = fieldLabel ? columns.find((col) => col.Label === fieldLabel)?.ValidationString : undefined;
			return (
				<div className="optionsContainer" key={`dataKeyMultiplier_${i}`}>
					<Dropdown
						className="selectDataKey"
						options={dropdownFields || []}
						placeholder={`Select data key...`}
						invert={true}
						isClearable={true}
						isSearchable={true}
						value={selectedOption.dataKey}
						onChange={(selected) => {
							this.handleDropdownChange(selected, i);
						}}
					>
						Data Key
					</Dropdown>
					{fieldLabel && !freeFormFields.includes(fieldLabel) ? (
						<>
							<Dropdown
								className="selectDataValue"
								options={fieldsWithOptions[fieldLabel] || []}
								placeholder={`Select single or multiple values...`}
								onChange={(selectedOption) => {
									this.handleMultiDropdownChange(selectedOption, i);
								}}
								invert={true}
								display={OPTION_DISPLAY.PILL_INLINE}
								multiple={true}
								isSearchable={true}
								value={selectedOption.dataValues}
							>
								Data Values
							</Dropdown>
							{this.createActions(i)}
						</>
					) : fieldLabel && freeFormFields.includes(fieldLabel) ? (
						<>
							<div className="textFieldContainer">
								<Textbox
									className="enterDataValueTextbox"
									id={`textField_${fieldLabel}`}
									placeholder={`Enter ${fieldLabel} value`}
									pattern={fieldValidationPattern || undefined}
									error={fieldValidationPattern ? VALIDATION_ERROR_MESSAGE : undefined}
									onKeyUp={(e) => this.handleTextFieldInput(e, i, fieldLabel)}
									onChange={(e) => this.handleTextFieldChange(e, i, fieldLabel)}
									value={this.state.freeFormFields[fieldLabel]}
								/>
								<div className="selectedDataValueText">
									{selectedOption.dataValues.map((value) => (
										<div id={`textInputValue_${value}`} className="textInputValue">
											<span>{value}</span>
											<i id={`deleteInputValue_${value}`} className="deleteInputValue icon-close" onClick={(e) => this.deleteInputValue(e, i, value)} />
										</div>
									))}
								</div>
							</div>
							{this.createActions(i)}
						</>
					) : (
						<>
							<Dropdown className="selectDataValue" invert={true} disabled={true} placeholder={`Select Field First`} value={null} options={[]}>
								Data Values
							</Dropdown>
							{this.createActions(i)}
						</>
					)}
				</div>
			);
		}
	};

	deleteInputValue = (e, i, removedValue) => {
		const newSelectedOptionsArray = this.state.selectedOptionsArray;
		newSelectedOptionsArray[i].dataValues = newSelectedOptionsArray[i].dataValues.filter((value) => value !== removedValue);
		this.setState({ selectedOptionsArray: newSelectedOptionsArray }, () => {
			this.calculateCount(newSelectedOptionsArray);
		});
	};

	addField = () => {
		const newSelectedOptionsArray = this.state.selectedOptionsArray;
		newSelectedOptionsArray.push({ dataKey: null, dataValues: [] });
		this.setState({ selectedOptionsArray: newSelectedOptionsArray });
	};

	removeField = (fieldIndex) => {
		const newSelectedOptionsArray = this.state.selectedOptionsArray;
		newSelectedOptionsArray.splice(fieldIndex, 1);
		this.setState({ selectedOptionsArray: newSelectedOptionsArray }, () => {
			this.calculateCount(newSelectedOptionsArray);
		});
	};

	calculateCount = (optionsArray) => {
		let count = 0;
		optionsArray.forEach((option, index) => {
			if (option.dataValues.length) {
				if (index === 0) {
					count += option.dataValues.length;
				} else {
					count *= option.dataValues.length;
				}
			}
		});
		this.setState({ multipliedRowsCount: count });
	};

	handleDropdownChange = (selectedOption, fieldID) => {
		const newSelectedOptionsArray = this.state.selectedOptionsArray;
		const newFreeformFields = this.state.freeFormFields;
		const currentLabel = newSelectedOptionsArray[fieldID]?.dataKey?.label || null;
		if (currentLabel && newFreeformFields[currentLabel]) {
			delete newFreeformFields[currentLabel];
		}
		if (selectedOption !== null) {
			newSelectedOptionsArray[fieldID].dataKey = selectedOption;
		} else if (selectedOption === null) {
			newSelectedOptionsArray[fieldID].dataKey = null;
		}
		newSelectedOptionsArray[fieldID].dataValues = [];
		this.setState({ selectedOptionsArray: newSelectedOptionsArray, freeFormFields: newFreeformFields }, () => {
			this.calculateCount(newSelectedOptionsArray);
		});
	};

	handleMultiDropdownChange = (selectedOption, fieldID) => {
		const newSelectedOptionsArray = this.state.selectedOptionsArray;
		newSelectedOptionsArray[fieldID].dataValues = selectedOption;
		this.setState({ selectedOptionsArray: newSelectedOptionsArray }, () => {
			this.calculateCount(newSelectedOptionsArray);
		});
	};

	handleTextFieldChange = (e, i, fieldLabel) => {
		let { value } = e.target;
		let validation = this.props.config.RegEx;
		let isSubTaxonomy = false;
		let fieldValidationString;
		this.props.config.MetaData.forEach((field) => {
			if (field.Label === fieldLabel) {
				if (field.IsSubTaxonomyKey === true) {
					isSubTaxonomy = true;
				}
				if (field.ValidationString) {
					fieldValidationString = field.ValidationString;
				}
			}
		});
		this.props.config.TaxonomyData.forEach((field) => {
			if (field.Label === fieldLabel) {
				if (field.IsSubTaxonomyKey === true) {
					isSubTaxonomy = true;
				}
				if (field.ValidationString) {
					fieldValidationString = field.ValidationString;
				}
			}
		});
		if (isSubTaxonomy === true) {
			if (this.props.config.SubTaxonomyRegEx && this.props.config.SubTaxonomyRegEx.length === 2) {
				validation = this.props.config.SubTaxonomyRegEx;
			}
		}
		if (!fieldValidationString) {
			if (validation !== null && validation.length !== 0) {
				let formattedValidation = validation[0].substring(validation[0].indexOf("["), validation[0].lastIndexOf("]") + 1);
				formattedValidation = formattedValidation.substring(0, 1) + "^" + formattedValidation.substring(1, formattedValidation.length);
				if (formattedValidation.indexOf("\\\\\\") !== -1) {
					formattedValidation =
						formattedValidation.substring(0, formattedValidation.indexOf("\\\\\\")) +
						"\\\\\\" +
						formattedValidation.substring(formattedValidation.indexOf("\\\\\\") + 1, formattedValidation.length);
				}
				value = value.replace(new RegExp(formattedValidation, "g"), "");
			} else if (validation === null || validation.length === 0) {
				value = value.replace(/[^a-zA-Z0-9-\\/| +]/g, "");
			}
		}
		if (validation !== null && validation.length !== 0 && validation[1] !== undefined) {
			if (value.length > parseInt(validation[1])) {
				value = value.substring(0, parseInt(validation[1]));
			}
		} else if (validation === null || validation.length === 0 || validation[1] === undefined) {
			if (value.length > 30) {
				value = value.substring(0, 30);
			}
		}
		const { freeFormFields } = this.state;
		freeFormFields[fieldLabel] = value;
		this.setState({ freeFormFields });
	};

	handleTextFieldInput = (event, i, fieldLabel) => {
		if (event.keyCode === 13) {
			const textBox = document.getElementById(`textField_${fieldLabel}`);
			const newSelectedOptionsArray = this.state.selectedOptionsArray;
			if (textBox.value !== "" && !newSelectedOptionsArray[i].dataValues.includes(textBox.value.trim())) {
				if (textBox.validity && !textBox.validity.valid) {
					return;
				}
				const { value } = textBox;
				newSelectedOptionsArray[i].dataValues.push(value.trim());
				const { freeFormFields } = this.state;
				freeFormFields[fieldLabel] = "";
				this.setState({ selectedOptionsArray: newSelectedOptionsArray, freeFormFields }, () => {
					this.calculateCount(newSelectedOptionsArray);
				});
			}
		}
	};

	checkIfDisableMultiply = () => {
		if (
			this.state.selectedOptionsArray.find((option) => {
				return !option.dataKey || option.dataValues.length === 0;
			})
		) {
			return true;
		}
		return false;
	};

	render() {
		return (
			<Modal
				className="multiplier_modal"
				id={`${this.props.name}_multiplier`}
				heading={`${this.props.name} Multiplier`}
				size={MODAL_SIZE.LARGE}
				description={
					<>
						Select a data key and any choose data values to create {this.props.name}s in bulk. You can make edits inline before saving
						<div className="multiplierCount">
							{this.props.name}s to be created: {this.state.multipliedRowsCount}
						</div>
					</>
				}
				onRequestClose={() => {
					this.modalCleanupOnClose();
				}}
				actionSetProps={{
					actionsRight: (
						<>
							<Button
								display={BUTTON_DISPLAY.SECONDARY}
								onClick={() => {
									this.modalCleanupOnClose();
								}}
								data-tracking-id-alt="cancel-multiplier"
							>
								Cancel
							</Button>
							<Button disabled={this.checkIfDisableMultiply()} onClick={this.performMultiplication} data-tracking-id-alt="confirm-multiplier">
								Multiply
							</Button>
						</>
					)
				}}
				onClick={() => {}}
			>
				<div className="multiplierDropdownContainer">{this.buildAdvancedOptions()}</div>
			</Modal>
		);
	}
}

export default MultiplierModal;
